import { useMemo } from 'react';
import { useOffersList } from '../../api/offers';
import type { ChatMessage } from '../../api/conversations';
import type { OfferItem } from '../gigs/my-gigs/types';
import { isOfferCardMessage, offerUuidFromMessage } from './offerCard';

const CLOCK_SKEW_MS = 5 * 60 * 1000;

function toMs(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Resolves the offer behind an offer chat card whose payload carries no offer id,
 * by matching gig offers in the same conversation against the message time.
 */
export function useConversationOffer(
  message: Pick<ChatMessage, 'card_type' | 'card_payload' | 'conversation_id' | 'created_at'>,
): string | undefined {
  const needsLookup =
    isOfferCardMessage(message) && !offerUuidFromMessage(message) && !!message.conversation_id;

  const created = useOffersList(
    { perspective: 'created', page_size: 100 },
    { enabled: needsLookup },
  );
  const received = useOffersList(
    { perspective: 'received', page_size: 100 },
    { enabled: needsLookup },
  );

  return useMemo(() => {
    if (!needsLookup) return undefined;
    const seen = new Set<string>();
    const matches: OfferItem[] = [];
    for (const offer of [...(created.data?.results ?? []), ...(received.data?.results ?? [])]) {
      if (seen.has(offer.id)) continue;
      seen.add(offer.id);
      if (!offer.gigUuid || offer.status === 'draft') continue;
      if (offer.conversationId !== message.conversation_id) continue;
      matches.push(offer);
    }
    if (matches.length === 0) return undefined;

    const messageMs = toMs(message.created_at);
    const withSent = matches
      .map((offer) => ({ offer, sentMs: toMs(offer.sentAt) }))
      .filter((m): m is { offer: OfferItem; sentMs: number } => m.sentMs != null)
      .sort((a, b) => b.sentMs - a.sentMs);

    if (withSent.length > 0) {
      if (messageMs == null) return withSent[0].offer.id;
      const before = withSent.find((m) => m.sentMs <= messageMs + CLOCK_SKEW_MS);
      return (before ?? withSent[withSent.length - 1]).offer.id;
    }
    return matches[0].id;
  }, [needsLookup, created.data, received.data, message.conversation_id, message.created_at]);
}
